import TileContentMaker from 'Components/DashboardTile/TileContentMaker';
import { fm } from 'Components/FormatMessage';
import PlatformConditional from 'Components/PlatformConditional';
import { calculatePackYearsFromDocuments, getBackgroundTimeseries } from 'neuro-calculation-commons';
import { isPartialDate, sortPartialDate } from 'neuro-utils';
import * as React from 'react';
import { useAppSelector } from 'Store/index';
import { formatPartialDate } from 'Utility/date';
import { mapDocumentToNeuroDocument } from 'Utility/documentHandling';
import { resolveWeightOrBmi, analyzeDrivingStatus } from './utils';

import type { DocTypesByPageName } from '../../../config/platformSettings';
import type { IDrivingStatus } from './utils';
import type { IData } from 'Components/DashboardTile/TileContentMaker/components/definitions';

/**
 * Filters out null/undefined/false items and returns a strict IData array.
 */
const compact = (...items: (IData | undefined | null | false)[]): IData[] => {
  return items.filter((item): item is IData => !!item);
};

// Sub-components

const DrivingBanContent = ({ status }: { status: IDrivingStatus }) => {
  if (!status.hasLicense || status.hasLicense === 'unknown') return null;

  const r1 = status.r1.active && (
    <div>
      {fm('background.drivingBanR1Short')} {fm('background.fromShort')}{' '}
      {status.r1.startDate ? formatPartialDate(status.r1.startDate) : '-'}
    </div>
  );
  const r2 = status.r2.active && (
    <div>
      {fm('background.drivingBanR2Short')} {fm('background.fromShort')}{' '}
      {status.r2.startDate ? formatPartialDate(status.r2.startDate) : '-'}
    </div>
  );

  if (!r1 && !r2) {
    return <>{status.licenseGroup.length > 0 ? fm('general.no') : fm('general.noData')}</>;
  }
  return (
    <>
      {r1}
      {r2}
    </>
  );
};

const LicenseContent = ({ status }: { status: IDrivingStatus }) => {
  if (status.licenseGroup.length > 0) {
    return (
      <>
        {status.licenseGroup.includes('R1') && fm('background.drivingBanR1Short')}
        {status.licenseGroup.includes('R1') && status.licenseGroup.includes('R2') && <span> {fm('general.and')} </span>}
        {status.licenseGroup.includes('R2') && fm('background.drivingBanR2Short')}
      </>
    );
  }
  if (status.hasLicense === true) return <>{fm('general.yes')}</>;
  if (status.hasLicense === false) return <>{fm('general.no')}</>;
  return <>{fm('general.unknown')}</>;
};

const NoProReqsContent = ({ reqs }: { reqs: IDrivingStatus['noProReqs'] }) => {
  if (reqs.length === 0) return <>{fm('general.noData')}</>;
  return (
    <>
      {reqs.map((d, i) => (
        <div key={i}>
          {fm('background.fromShort')} {formatPartialDate(d.startDate)}
        </div>
      ))}
    </>
  );
};

// Main component

const BackgroundDash = ({ documents }: IOwnProps): React.JSX.Element | undefined => {
  const platform = useAppSelector(s => s.session.platforms?.selected);
  const myBgDocs = useAppSelector(s => s.myapp.sortedAndMergedDocuments).filter(d =>
    d._type.toLowerCase().includes('background'),
  );

  // Data processing

  const { series, driving, packYears } = React.useMemo(() => {
    // 1. Get the timeseries
    const { series } = getBackgroundTimeseries(
      documents.map(mapDocumentToNeuroDocument),
      myBgDocs.map(mapDocumentToNeuroDocument),
    );

    // 2. Prepare background docs for specific calculations
    const allDocs = [...documents, ...myBgDocs];
    const bgDocs = allDocs.filter((d): d is IDocumentWithTypeV2<'background'> => d._type === 'background');
    bgDocs.sort((a, b) => sortPartialDate(b.date, a.date)); // Sort newest first

    const latestBg = bgDocs[0]; // The most recent background document
    const neuroDocs = bgDocs.map(mapDocumentToNeuroDocument); // Map for pack years math

    // 3. Calculate specific dashboard states
    return {
      series,
      packYears: calculatePackYearsFromDocuments(neuroDocs),
      driving: analyzeDrivingStatus(latestBg),
    };
  }, [documents, myBgDocs]);

  // Row builder utilities

  const latestItem = (key: string) => series[key]?.[0];

  /** Safe/trict Row Builder */
  const row = {
    /**
     * Base builder.
     * Ensures value is compatible with IData (React.JSX.Element | string | number).
     */
    make: (key: string, titleId: string, value: React.ReactNode, dateStr: string = ''): IData => ({
      key,
      title: fm(titleId),
      value: React.isValidElement(value) ? value : <>{value}</>,
      date: dateStr,
    }),

    /**
     * For simple string/number values.
     * Returns undefined if value is missing or wrong type.
     */
    simple: (key: string, titleId: string, unitId?: string): IData | undefined => {
      const item = latestItem(key);
      const val = item?.value;
      if (!item || (typeof val !== 'string' && typeof val !== 'number')) return;

      const display = unitId ? fm(unitId, { value: val }) : val;
      const date = isPartialDate(item.date) ? formatPartialDate(item.date) : '';
      return row.make(key, titleId, display, date);
    },

    /**
     * For enum values (handles both string and string[]).
     */
    enum: (key: string, titleId: string, prefix: string, showDate: boolean = true): IData | undefined => {
      const item = latestItem(key);
      if (!item) return;

      // Handle array
      if (Array.isArray(item.value)) {
        if (item.value.length === 0) return;
        if (item.value.length > 1) return row.make(key, titleId, fm('background.multiple'), '');
        return row.make(key, titleId, fm(`${prefix}.${item.value[0]}`), '');
      }

      // Handle single
      const date = showDate && isPartialDate(item.date) ? formatPartialDate(item.date) : '';
      return row.make(key, titleId, fm(`${prefix}.${item.value}`), date);
    },

    /**
     * For complex components (driving status etc).
     */
    custom: (key: string, titleId: string, component: React.ReactNode): IData => {
      return row.make(key, titleId, component, '');
    },
  };

  // Data group helpers
  // These return arrays of (IData | undefined) to be spread into compact()

  const getWeightBmiRows = (): (IData | undefined)[] => {
    const types = resolveWeightOrBmi(series);
    return [
      types.includes('weight') ? row.simple('weight', 'background.weight', 'background.kg') : undefined,
      types.includes('bmi') ? row.simple('bmi', 'background.bmi', 'background.bmi') : undefined,
      row.simple('height', 'background.height', 'background.cm'),
    ];
  };

  const getEmploymentRows = (): (IData | undefined)[] => {
    const empVal = latestItem('employment')?.value;
    const isValidEmp = empVal && !isPartialDate(empVal) && (!Array.isArray(empVal) || empVal.length > 0);

    const reimbItem = latestItem('reimbursementValidUntil');
    const reimbVal = reimbItem?.value;

    return [
      isValidEmp ? row.enum('employment', 'background.employment', 'background.opts.employment') : undefined,
      row.enum('typeOfLiving', 'background.typeOfLiving', 'background.opts.typeOfLiving'),
      isPartialDate(reimbVal)
        ? row.make(
            'reimbursement',
            'background.reimbursement',
            fm('background.dueDate', { value: formatPartialDate(reimbVal) }),
            '',
          )
        : undefined,
    ];
  };

  const getWorkAbilityRow = (): IData | undefined => {
    const empVal = latestItem('employment')?.value;
    const scoreItem = latestItem('workAbilityScore');
    const scoreVal = scoreItem?.value;

    // Typeguard: Ensure scoreVal is an object with the specific property
    const hasScore =
      scoreVal &&
      typeof scoreVal === 'object' &&
      !Array.isArray(scoreVal) &&
      'workAbilityComparedToLifetimeBest' in scoreVal;

    if (!empVal || !hasScore) return;

    return row.make('workAbilityScore', 'background.workAbility', `${scoreVal.workAbilityComparedToLifetimeBest} / 10`);
  };

  const getHuntingtonRows = (): (IData | undefined)[] => {
    const famVal = latestItem('familyMembersAnamnesisHuntington')?.value;
    const hasFamilyData = Array.isArray(famVal) && famVal.length > 0;

    /** TODO what to do when latestItem('familyAnamnesisHuntington') is undefined */
    const anamnesisVal = hasFamilyData ? (
      fm('background.multiple')
    ) : !latestItem('familyAnamnesisHuntington')?.value ? (
      <>-</>
    ) : (
      fm(`background.opts.yesNoUnknown.${latestItem('familyAnamnesisHuntington')?.value}`)
    );

    return [
      row.make('familyAnamnesis', 'background.familyAnamnesis', anamnesisVal),
      row.enum('maritalStatus', 'background.maritalStatus', 'background.opts.maritalStatus'),
      ...getEmploymentRows(),
    ];
  };

  const getPolyneuroRows = (): (IData | undefined)[] => {
    const bgData = documents.filter(d => d._type === 'background').sort((a, b) => sortPartialDate(b.date, a.date));
    if (!bgData[0] || typeof bgData[0] !== 'object') return [];
    const { dominantArm, rarePolyneuropathyInTheFamily, rarePolyneuropathyInFirstDegreeRelatives } = bgData[0];
    return [
      row.make(
        'rarePolyneuropathyInTheFamily',
        'background.rarePolyneuropathyInTheFamily',
        rarePolyneuropathyInTheFamily ? fm('background.opts.yesNoUnknown.' + rarePolyneuropathyInTheFamily) : '-',
      ),
      row.make(
        'rarePolyneuropathyInFirstDegreeRelatives',
        'background.rarePolyneuropathyInFirstDegreeRelatives',
        rarePolyneuropathyInFirstDegreeRelatives
          ? fm('background.opts.yesNoUnknown.' + rarePolyneuropathyInFirstDegreeRelatives)
          : '-',
      ),
      row.make('familyAnamnesis', 'background.dominantArm', fm('background.opts.dominantArm.' + dominantArm)),
    ];
  };

  const getDrivingRows = (includePro: boolean): (IData | undefined)[] => {
    if (driving.hasLicense === 'unknown') return [];

    return [
      row.custom('driversLicense', 'background.driversLicense', <LicenseContent status={driving} />),
      includePro ? row.simple('professionalDriving', 'background.professionalDriving') : undefined,
      row.custom('ongoingDrivingBan', 'background.ongoingDrivingBan', <DrivingBanContent status={driving} />),
      includePro
        ? row.custom(
            'noProDrivingRequirements',
            'background.noProDrivingRequirements',
            <NoProReqsContent reqs={driving.noProReqs} />,
          )
        : undefined,
    ];
  };

  return (
    <React.Fragment>
      <PlatformConditional platform={['sma', 'dmd', 'mgravis']}>
        <TileContentMaker
          type="specificList"
          data={compact(
            row.simple('weight', 'background.weight', 'background.kg'),
            row.simple('height', 'background.height', 'background.cm'),
            // SMA / DMD
            platform !== 'mgravis'
              ? row.simple('headCircumference', 'background.headCircumference', 'background.cm')
              : undefined,
            // Mgravis
            platform === 'mgravis' ? row.enum('smoking', 'background.smoking', 'background.opts.smoking') : undefined,
            platform === 'mgravis' ? row.enum('snus', 'background.snusTitle', 'background.opts.snus') : undefined,
            platform === 'mgravis' ? getWorkAbilityRow() : undefined,
          )}
        />
      </PlatformConditional>

      <PlatformConditional platform={['ninmt']}>
        <TileContentMaker
          type="specificList"
          data={compact(
            row.simple('gaf', 'background.gaf'),
            row.simple('madrs', 'background.madrs'),
            row.simple('ybocs', 'background.ybocsTitle'),
          )}
        />
      </PlatformConditional>

      <PlatformConditional platform={['ms', 'parkinson', 'epilepsy']}>
        <TileContentMaker type="specificList" data={compact(...getWeightBmiRows())} />

        <TileContentMaker
          type="specificListNoDate"
          data={compact(
            ...getEmploymentRows(),
            // MS
            platform === 'ms' ? row.enum('smoking', 'background.smoking', 'background.opts.smoking') : undefined,
            // Epilepsy
            // Spread the result of the ternary to avoid passing boolean|array to compact
            ...(platform === 'epilepsy' ? getDrivingRows(false) : []),
          )}
        />
      </PlatformConditional>

      <PlatformConditional platform="sleepApnea">
        <TileContentMaker
          type="specificListNoDate"
          data={compact(
            ...getDrivingRows(true),
            packYears > 0 ? row.make('packYears', 'background.packYears', packYears) : undefined,
          )}
        />
      </PlatformConditional>

      <PlatformConditional platform={['huntington']}>
        <TileContentMaker type="specificListNoDate" data={compact(...getHuntingtonRows())} />
      </PlatformConditional>

      <PlatformConditional platform={['polyneuro']}>
        <TileContentMaker type="specificListNoDate" data={compact(...getPolyneuroRows())} />
      </PlatformConditional>
    </React.Fragment>
  );
};

interface IOwnProps {
  documents: IDocumentWithTypeV2<DocTypesByPageName['background']>[];
}

export default BackgroundDash;
